<template>
  <ul
    :class="
      isCompactNavEnabled
        ? '-mb-4 flex h-52 items-center gap-32 p-4'
        : 'flex h-full items-center gap-32 px-4'
    "
  >
    <li v-if="isBuybackCmsPagesEnabled" class="h-full">
      <NavBarLink
        :label="i18n(translations.tradeIn)"
        :to="tradeInUrl"
        :tracking="{ zone: 'mega_menu', name: 'trade_in', value: 'trade_in' }"
        :icon="IconTradeIn"
        :compact="isCompactNavEnabled"
      />
    </li>

    <li v-if="cards.length" class="h-full">
      <NavBarDropdown :id="CARE_DROPDOWN_ID">
        <template #trigger="{ isOpen, showMenu, hideMenu }">
          <button
            :aria-controls="`submenu-${CARE_DROPDOWN_ID}`"
            :aria-expanded="isOpen"
            aria-haspopup="true"
            class="text-action-default-hi flex h-full items-center border-0 bg-transparent p-0 text-nowrap hover:no-underline"
            :class="
              isCompactNavEnabled
                ? 'caption lg:body-2 hover:caption-bold lg:hover:body-2-bold'
                : 'body-2 hover:body-2-bold'
            "
            role="menuitem"
            type="button"
            @click="isOpen ? hideMenu() : showMenu()"
          >
            <div
              class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
              :class="{
                'after:opacity-100': isOpen,
                'gap-4 lg:gap-6': isCompactNavEnabled,
              }"
            >
              <IconQuality :size="isCompactNavEnabled ? '20' : undefined" />
              <BoldHack
                :text="i18n(translations.extendedServices)"
                :compact="isCompactNavEnabled"
              />
            </div>
          </button>
        </template>

        <template #panel="{ hideMenu }">
          <div class="border-static-default-low border-t">
            <div
              class="mx-auto grid max-w-[1120px] grid-cols-3 gap-12 pb-40 pt-24"
            >
              <NavServiceCard
                v-for="(card, index) in cards"
                :key="card.title"
                :cta-label="card.ctaLabel"
                :cta-url="card.ctaUrl"
                :description="card.description"
                :image="card.image"
                :position="index + 1"
                :title="card.title"
                :tracking="{
                  zone: 'mega_menu',
                  name: 'services',
                  value: card.trackingValue,
                }"
                @click-item="hideMenu"
              />
            </div>
          </div>
        </template>
      </NavBarDropdown>
    </li>

    <li v-if="mobilePlansUrl" class="h-full">
      <NavBarLink
        :label="i18n(translations.mobilePlans)"
        :to="mobilePlansUrl"
        :tracking="{
          zone: 'mega_menu',
          name: 'mobile_plans',
          value: 'mobile_plans',
        }"
        :icon="IconSmartphone"
        :compact="isCompactNavEnabled"
      />
    </li>
    <li v-if="isJapanMarket" class="h-full">
      <NavBarLink
        :label="i18n(translations.whatIsRefurbished)"
        :to="qualityPageUrl"
        :tracking="{
          zone: 'mega_menu',
          name: 'what_is_refurbished',
          value: 'what_is_refurbished',
        }"
        :icon="IconBackMarketPromise"
        :compact="isCompactNavEnabled"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconBackMarketPromise } from '@ds/icons/IconBackMarketPromise'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconSmartphone } from '@ds/icons/IconSmartphone'
import { IconTradeIn } from '@ds/icons/IconTradeIn'

import { useJapanNavigation } from '~/scopes/navigation/composables/useJapanNavigation'

import { useExtendedServicesCards } from '../../composables/useExtendedServicesCards'
import { useMobilePlansUrl } from '../../composables/useMobilePlansUrl'
import { useTradeInUrl } from '../../composables/useTradeInUrl'

import BoldHack from './BoldHack.vue'
import NavBarDropdown from './NavBarDropdown.vue'
import NavBarLink from './NavBarLink.vue'
import NavServiceCard from './NavServiceCard.vue'
import translations from './NavServicesMenu.translations'

const CARE_DROPDOWN_ID = 'services-care-and-repair'

const i18n = useI18n()

const { tradeInUrl } = useTradeInUrl()
const { mobilePlansUrl } = useMobilePlansUrl()
const { cards } = useExtendedServicesCards()
const { isBuybackCmsPagesEnabled } = useBuybackEnabled()
const { isJapanMarket, qualityPageUrl } = useJapanNavigation()

const experiments = useExperiments()
const isCompactNavEnabled = computed(
  () => experiments['experiment.desktopNavStyling'] === 'compact',
)
</script>
